import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";

export default function BookCard({ book, variant = "library" }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const from  = book.coverColor?.[0] || book.colorFrom || "#1a1108";
  const to    = book.coverColor?.[1] || book.colorTo   || "#7a3d12";
  const saved = typeof window !== "undefined" ? localStorage.getItem(`bookmark_${book.id}`) : null;
  const progress = saved ? Math.min(Math.round((parseInt(saved) / (book.pages || 300)) * 100), 100) : 0;

  return (
    <div
      onClick={() => navigate(`/book/${book.id}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "var(--card-bg)",
        borderRadius: "var(--radius-lg)",
        border: `1px solid ${hovered ? "var(--card-hover-border)" : "var(--card-border)"}`,
        overflow: "hidden",
        cursor: "pointer",
        boxShadow: hovered ? "var(--shadow-lg)" : "var(--shadow)",
        transform: hovered ? "translateY(-6px) scale(1.012)" : "translateY(0) scale(1)",
        transition: "transform 0.28s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.28s ease, border-color 0.2s ease",
        animation: "fadeUp 0.4s ease both",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* ── Cover ── */}
      <div style={{
        height: "230px",
        background: `linear-gradient(155deg, ${from} 0%, ${to} 100%)`,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem 1.25rem 1.75rem",
      }}>
        {/* Spine */}
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "6px", background: "rgba(0,0,0,0.3)" }} />
        {/* Gloss */}
        <div style={{ position: "absolute", top: 0, left: "6px", right: 0, height: "45%", background: "linear-gradient(180deg,rgba(255,255,255,0.08) 0%,transparent 100%)" }} />
        {/* Circle deco */}
        <div style={{ position: "absolute", bottom: "-30px", right: "-30px", width: "100px", height: "100px", borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />

        {/* Badge */}
        {book.badge && (
          <div style={{
            position: "absolute", top: "10px", right: "10px",
            background: "var(--gold)", color: "#fff",
            fontSize: "0.56rem", fontWeight: 700,
            padding: "3px 8px", borderRadius: "var(--radius-full)",
            letterSpacing: "0.08em", textTransform: "uppercase",
            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          }}>{book.badge}</div>
        )}

        {/* Free badge */}
        {!book.price && (
          <div style={{
            position: "absolute", top: "10px", left: "16px",
            background: "var(--sage)", color: "#fff",
            fontSize: "0.56rem", fontWeight: 700,
            padding: "3px 8px", borderRadius: "var(--radius-full)",
            letterSpacing: "0.06em", textTransform: "uppercase",
          }}>FREE</div>
        )}

        {/* Icon */}
        <div style={{
          fontSize: "2.2rem",
          marginBottom: "0.7rem",
          filter: "drop-shadow(0 3px 10px rgba(0,0,0,0.4))",
          zIndex: 1,
        }}>📖</div>

        {/* Title on cover */}
        <div style={{
          fontFamily: "var(--font-display)",
          color: "#fff",
          fontSize: "1.05rem",
          fontWeight: 700,
          textAlign: "center",
          lineHeight: 1.35,
          textShadow: "0 2px 10px rgba(0,0,0,0.45)",
          zIndex: 1,
        }}>{book.title}</div>

        {/* Progress bar if reading */}
        {progress > 0 && (
          <div style={{ position: "absolute", bottom: 0, left: "6px", right: 0, height: "3px", background: "rgba(255,255,255,0.15)" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: "var(--gold-light)", transition: "width 0.4s ease" }} />
          </div>
        )}

        {/* Hover overlay — simple "View Details" strip at bottom */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          padding: "28px 0 12px",
          background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.22s ease",
          pointerEvents: "none",
        }}>
          <span style={{
            color: "#fff", fontSize: "0.75rem", fontWeight: 600,
            letterSpacing: "0.04em",
            transform: hovered ? "translateY(0)" : "translateY(5px)",
            transition: "transform 0.22s ease",
            display: "inline-flex", alignItems: "center", gap: "4px",
          }}>
            View Details <span style={{ fontSize: "0.65rem" }}>→</span>
          </span>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ padding: "0.85rem 1rem", flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
          <span style={{
            fontSize: "0.78rem", color: "var(--muted)", fontWeight: 400,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            flex: 1,
          }}>{book.author}</span>
          {book.genre && (
            <span style={{
              fontSize: "0.58rem", color: "var(--gold)",
              background: "var(--gold-faint)", border: "1px solid var(--gold-faint-2)",
              padding: "2px 8px", borderRadius: "var(--radius-full)",
              fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
              whiteSpace: "nowrap", flexShrink: 0,
            }}>{book.genre}</span>
          )}
        </div>

        {/* Rating */}
        {book.rating > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {[1,2,3,4,5].map((s) => (
              <Star key={s} size={10} fill={s <= Math.round(book.rating) ? "var(--gold)" : "none"} color={s <= Math.round(book.rating) ? "var(--gold)" : "var(--dust-2)"} />
            ))}
            <span style={{ fontSize: "0.65rem", color: "var(--muted)", marginLeft: "2px" }}>{book.rating?.toFixed(1)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
